import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MistService } from '@app/features/mists/shared/mist.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Mist } from '@app/shared/models/mist/mist.model';
import { MistLocation } from '@app/shared/models/mist/mist-location';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-chain-wide-mists',
    templateUrl: './chain-wide-mists.component.html',
    styleUrls: ['./chain-wide-mists.component.css'],
    standalone: false
})
export class ChainWideMistsComponent implements OnInit {
    @ViewChild('mistsTable') mistsTable: MatTable<Mist>;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    isLoading = false;
    onlyShowOffline: boolean;
    hideAllAPsOffline: boolean;
    hideLabs: boolean;
    hideFutures: boolean;
    hideRemodels: boolean;
    filterText: string = null;
    allMists = [];
    mistLocations = [];
    locations = [];
    displayedColumns = ['storeNumber', 'locationName', 'locationTimeZone', 'locationStatus', 'mists'];
    dataSource = new MatTableDataSource<MistLocation>(this.mistLocations);

    constructor(private mistsService: MistService, private locationsService: LocationsService) { }

    ngOnInit() {
        this.initComponent();
    }

    private async initComponent() {
        this.isLoading = true;
        await this.createDataSource();
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'storeNumber': return item.location.number;
                case 'locationName': return item.location.name;
                case 'locationTimeZone': return item.location.timezone;
                case 'locationStatus': return item.location.status;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
        this.mistsTable.renderRows();
        
        this.isLoading = false;
    }

    async getAllMists() {
        try {
            const observable = await this.mistsService.getAllMistsLatent();
            const mists_from_response = await firstValueFrom(observable);
            if (typeof mists_from_response === 'string') {
                console.log("Mists timed out!")
                return;
            }
            mists_from_response["message"].forEach(mist_json => {
                let mist: Mist = mist_json;
                this.allMists.push(mist);
            });
        } catch (error) {
            console.log("Mists timed out!")
        }
    }

    async getAllLocations() {
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                this.locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
    }

    sortMists() {
        let sortedMists = new Map<string, Mist[]>();
        for (let mist of this.allMists as Mist[]) {
            let storeNumber = "";

            if (mist.name != null) {
                storeNumber = mist.name.substring(0,5);
            }
            
            if (sortedMists.has(storeNumber)) {
                sortedMists.get(storeNumber).push(mist);
            } else {
                let mists = [];
                mists.push(mist);
                sortedMists.set(storeNumber, mists);
            }
        }
        return sortedMists;
    }

    sortLocations() {
        let sortedLocations = new Map<string, Location>();
        this.locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?) {

        if (locations == null) {
            await this.getAllLocations();
        } else {
            this.locations = locations;
        }

        await this.getAllMists();

        let sortedMists = this.sortMists();
        let sortedLocation = this.sortLocations();

        sortedMists.forEach((value: Mist[], key: string) => {
            if (sortedLocation.has(key)) {
                let mistLocation: MistLocation = new MistLocation;
                mistLocation.mists = value;
                mistLocation.location = sortedLocation.get(key);
                this.mistLocations.push(mistLocation);
            }
        });
    }

    onToggle() {
        this.applyFilter(this.filterText);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterPredicate = (data: MistLocation, filter: string) => {
            let hasOfflineMists = false;
            let hasOnlineMists = false;
            let containsFilterValue = false;

            // Handle offline fortigates
            if (this.onlyShowOffline) {
                for (let mist of data.mists) {
                    if (mist.connected == false) {
                        hasOfflineMists = true;
                    }
                }
            }

            if (this.hideAllAPsOffline) {
                for (let mist of data.mists) {
                    if (mist.connected == true) {
                        hasOnlineMists = true;
                    }
                }
            }

            // Handle filter text filtering
            if (filter == "null"
                || data.location.number.toLowerCase().includes(filterValue)
                || data.location.name.toLowerCase().includes(filterValue)
                || data.location.timezone.toLowerCase().includes(filterValue)
                || data.location.status.toLowerCase().includes(filterValue)) {
                containsFilterValue = true;
            }

            // Coalesce and make filtering decision
            if (this.hideLabs && data.location.number.charAt(0) == "9") {
                return false;
            } else if (this.hideFutures && data.location.status == "future") {
                return false;
            } else if (this.hideRemodels && data.location.status == "remodel") {
                return false;
            } else if (!containsFilterValue) {
                return false
            } else if (this.hideAllAPsOffline && !hasOnlineMists) {
                return false;
            } else if (this.onlyShowOffline && !hasOfflineMists) {
                return false;
            } else {
                return true;
            }
        }

        if (filterValue == null || filterValue == "") { // handles case where no filter text is provided
            this.dataSource.filter = "null";
        } else {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }

}